import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@arcadeai/design-system/components/ui/atoms/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { addDays, format } from "date-fns";
import { AlertTriangle, Loader2, Plus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useCreateApiKey } from "@/app/api-keys/api/hooks";
import { CustomDatePickerDialog } from "@/app/api-keys/components/create/custom-date-picker-dialog";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";

const schema = z
  .object({
    label: z.string().min(1, "Label is required"),
    expiresAt: z.date().optional(),
  })
  .refine(
    (data) => {
      if (data.expiresAt) {
        return data.expiresAt > new Date();
      }
      return true;
    },
    {
      message: "Expiration date must be in the future",
      path: ["expiresAt"],
    }
  );

type ApiKeyFormData = z.output<typeof schema>;

export const CreateApiKeyDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCustomDialogOpen, setIsCustomDialogOpen] = useState(false);
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const { mutate: createApiKey, isPending } = useCreateApiKey(
    projectId,
    organizationId
  );
  const [apiKey, setApiKey] = useState<string | null>(null);

  // Initialize the form with react-hook-form and zod
  const form = useForm<ApiKeyFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      label: "",
      expiresAt: addDays(new Date(), 30),
    } satisfies Partial<ApiKeyFormData>,
  });

  // Helper functions
  const getExpirationSelectValue = (date: Date | undefined): string => {
    if (!date) {
      return "never";
    }

    const now = new Date();
    const daysDiff = Math.round(
      (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    const presets = [7, 30, 60, 90];
    if (presets.includes(daysDiff)) {
      return String(daysDiff);
    }

    return "custom";
  };

  const formatExpirationDescription = (date: Date | undefined): string => {
    if (!date) {
      return "This API key will never expire";
    }
    const formatted = date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
    return `This API key will expire on ${formatted}`;
  };

  const expiresAt = form.watch("expiresAt");
  const selectValue = getExpirationSelectValue(expiresAt);
  const customLabel =
    selectValue === "custom" && expiresAt
      ? `Custom (${format(expiresAt, "MMM d, yyyy")})`
      : "Custom";

  const expirationOptions: { value: string; label: string }[] = [
    { value: "7", label: "7 days" },
    { value: "30", label: "30 days (recommended)" },
    { value: "60", label: "60 days" },
    { value: "90", label: "90 days" },
    { value: "custom", label: customLabel },
    { value: "never", label: "No expiration" },
  ];

  const selectedExpirationLabel =
    expirationOptions.find((o) => o.value === selectValue)?.label ?? "";

  const onSubmit = (data: ApiKeyFormData) => {
    createApiKey(
      {
        label: data.label,
        ...(data.expiresAt && {
          expiresAt: data.expiresAt.toISOString(),
        }),
      },
      {
        onSuccess: (response) => {
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          const typed = response as { data: { api_key: string } };
          setApiKey(typed.data.api_key);
          form.reset({
            label: "",
            expiresAt: addDays(new Date(), 30),
          });
        },
      }
    );
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      form.reset({
        label: "",
        expiresAt: addDays(new Date(), 30),
      });
      setApiKey(null);
      setIsCustomDialogOpen(false);
    }
  };

  const handleExpirationChange = (value: string | null) => {
    if (value == null) {
      return;
    }
    switch (value) {
      case "7":
        form.setValue("expiresAt", addDays(new Date(), 7));
        break;
      case "30":
        form.setValue("expiresAt", addDays(new Date(), 30));
        break;
      case "60":
        form.setValue("expiresAt", addDays(new Date(), 60));
        break;
      case "90":
        form.setValue("expiresAt", addDays(new Date(), 90));
        break;
      case "custom":
        setIsCustomDialogOpen(true);
        break;
      case "never":
        form.setValue("expiresAt", undefined);
        break;
      default:
        // Default to 30 days
        form.setValue("expiresAt", addDays(new Date(), 30));
        break;
    }
  };

  return (
    <CanProject permission={PROJECT.apiKeys.create}>
      <Dialog onOpenChange={handleDialogOpenChange} open={isOpen}>
        <DialogTrigger render={<Button size="sm" />}>
          <Plus className="mr-2 size-4" />
          Create API Key
        </DialogTrigger>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader className="space-y-1">
            <DialogTitle>
              {apiKey ? "API Key Created" : "Generate a New API Key"}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {apiKey
                ? "This is the only time you'll see this API key. Make sure to copy it now and store it safely."
                : "Fill out the form to create a new API key."}
            </DialogDescription>
          </DialogHeader>
          <div>
            {apiKey ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3">
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950/50">
                    <div className="flex items-center gap-3 p-4">
                      <AlertTriangle className="h-5 w-5 shrink-0 text-yellow-600 dark:text-yellow-500" />
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        API keys are administrator credentials. Anyone who has
                        your API key can make requests to Arcade as you. Always
                        store your API keys in a safe place, such as system
                        environment variables, and don't use them in browser or
                        frontend code.
                      </p>
                    </div>
                  </div>
                  <div className="rounded-lg border bg-muted/50">
                    <div className="flex items-center gap-3 p-3">
                      <code className="sensitive grow break-all font-mono text-sm">
                        {apiKey}
                      </code>
                      <CopyButton
                        className="shrink-0"
                        content={apiKey}
                        variant="ghost"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={() => setIsOpen(false)} size="sm">
                    Done
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <Form {...form}>
                <form
                  className="mt-1 flex flex-col gap-4"
                  onSubmit={form.handleSubmit(onSubmit)}
                >
                  <div className="flex flex-col gap-4">
                    <FormField
                      control={form.control}
                      name="label"
                      render={({ field }) => (
                        <FormItem className="w-full space-y-2">
                          <FormLabel>Label</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              autoFocus
                              placeholder="My API Key"
                            />
                          </FormControl>
                          <FormMessage />
                          <FormDescription>
                            Provide a name to easily identify this key later.
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="expiresAt"
                      render={() => (
                        <FormItem className="space-y-2">
                          <FormLabel>Expiration</FormLabel>
                          <Select
                            onValueChange={handleExpirationChange}
                            value={selectValue}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue>
                                  {() => selectedExpirationLabel}
                                </SelectValue>
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {expirationOptions.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                          <FormDescription>
                            {formatExpirationDescription(expiresAt)}
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                  <DialogFooter>
                    <DialogClose
                      render={
                        <Button size="sm" type="button" variant="outline" />
                      }
                    >
                      Cancel
                    </DialogClose>
                    <Button disabled={isPending} size="sm" type="submit">
                      {isPending ? (
                        <>
                          <Loader2 className="size-4 animate-spin" />
                          Creating API Key
                        </>
                      ) : (
                        "Create API Key"
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            )}
          </div>
        </DialogContent>

        <CustomDatePickerDialog
          initialDate={expiresAt || addDays(new Date(), 30)}
          onConfirm={(date) => {
            form.setValue("expiresAt", date);
          }}
          onOpenChange={setIsCustomDialogOpen}
          open={isCustomDialogOpen}
        />
      </Dialog>
    </CanProject>
  );
};
